import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { fetchJsonCached, getCachedJson } from '../utils/portalQueryCache';
import { getPlatformScreenshotSources, openPlatformTool } from '../utils/platformNavigation';
import { TOOLS_CACHE_URL, usePlatformAccessChanged } from '../utils/platformAccessSync';

function PlatformScreenshot({ tool }) {
  const sources = useMemo(() => getPlatformScreenshotSources(tool), [tool]);
  const [src, setSrc] = useState(() => sources?.primary || '');
  useEffect(() => {
    setSrc(sources?.primary || '');
  }, [sources?.primary]);
  if (!sources?.primary) return null;

  return (
    <div className="platform-card-media" aria-hidden="true">
      <img
        className="platform-card-screenshot"
        src={src}
        alt=""
        loading="lazy"
        onError={() => {
          if (!sources?.fallback || src === sources.fallback) {
            return;
          }
          setSrc(sources.fallback);
        }}
      />
      <div className="platform-card-media-overlay" />
    </div>
  );
}

export function ToolsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const cachedTools = getCachedJson(TOOLS_CACHE_URL);
  const [tools, setTools] = useState(() => (Array.isArray(cachedTools?.tools) ? cachedTools.tools : []));
  const [role, setRole] = useState(() => cachedTools?.role || user?.role || '');
  const [error, setError] = useState('');
  const [opening, setOpening] = useState(null); // { tool, rect }
  const openingTimerRef = useRef(0);

  const applyToolsPayload = useCallback((data) => {
    setTools(Array.isArray(data?.tools) ? data.tools : []);
    if (data?.role) setRole(data.role);
  }, []);

  const reloadTools = useCallback(() => {
    fetchJsonCached(TOOLS_CACHE_URL, { ttlMs: 60_000 })
      .then(applyToolsPayload)
      .catch(() => null);
  }, [applyToolsPayload]);

  usePlatformAccessChanged(reloadTools);

  useEffect(() => {
    if (user?.role && !role) {
      setRole(user.role);
    }
  }, [user?.role, role]);

  useEffect(() => {
    let isCancelled = false;
    const controller = new AbortController();
    const load = async () => {
      try {
        const data = await fetchJsonCached(TOOLS_CACHE_URL, { ttlMs: 60_000, signal: controller.signal });
        if (isCancelled) return;
        applyToolsPayload(data);
        setError('');
      } catch (e) {
        if (isCancelled) return;
        setError('Failed to load platforms list.');
      }
    };

    void load();

    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [applyToolsPayload]);

  useEffect(() => {
    const links = [];
    const addPreconnect = (origin) => {
      if (!origin) return;
      const href = String(origin).trim();
      if (!href) return;
      const existing = document.querySelector(`link[rel="preconnect"][href="${href}"]`);
      if (existing) return;
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
      links.push(link);
    };

    for (const tool of tools) {
      const rawUrl = String(tool?.url || '').trim();
      if (!rawUrl || (!rawUrl.startsWith('http://') && !rawUrl.startsWith('https://'))) continue;
      try {
        const u = new URL(rawUrl);
        addPreconnect(u.origin);
      } catch {
        // ignore malformed URLs from catalog
      }
    }

    return () => {
      for (const link of links) {
        if (link?.parentNode) {
          link.parentNode.removeChild(link);
        }
      }
    };
  }, [tools]);

  useEffect(() => {
    return () => {
      if (openingTimerRef.current) {
        window.clearTimeout(openingTimerRef.current);
      }
    };
  }, []);

  const openTool = (event, tool) => {
    if (!tool || opening) return;
    const target = event?.currentTarget;
    const rect = target?.getBoundingClientRect?.();
    if (!rect) return;

    const rawUrl = String(tool.url || '').trim();
    const url = rawUrl && !rawUrl.startsWith('http://') && !rawUrl.startsWith('https://') && !rawUrl.startsWith('/')
      ? `/${rawUrl}`
      : rawUrl;
    if (!url) return;

    if (url.startsWith('http://') || url.startsWith('https://')) {
      openPlatformTool(tool, navigate);
      return;
    }

    setOpening({
      tool,
      rect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
    });
    openingTimerRef.current = window.setTimeout(() => {
      navigate(url);
    }, 60);
  };

  const visibleTools = useMemo(() => tools.filter((t) => t && t.key), [tools]);

  if (error) {
    return <p className="error-text">{error}</p>;
  }

  const headingRole = role || user?.role || '';
  return (
    <section>
      <div className="section-heading">
        <div>
          <h2>Platforms{headingRole ? ` for ${headingRole}` : ''}</h2>
        </div>
      </div>
      <div className="tools-grid">
        {visibleTools.map((tool) => (
          <motion.article
            key={tool.key}
            className="tool-card"
            role="button"
            tabIndex={0}
            onClick={(event) => openTool(event, tool)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openTool(event, tool);
              }
            }}
            whileHover={opening ? undefined : { scale: 1.04, y: -5 }}
            whileTap={opening ? undefined : { scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 320, damping: 22 }}
          >
            <PlatformScreenshot tool={tool} />
            <div className="tool-card-row">
              <h3>{tool.name}</h3>
            </div>
            <p>{tool.description}</p>
          </motion.article>
        ))}
      </div>

      {opening?.tool && opening?.rect && (
        <>
          <motion.div
            className="tool-open-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.14, ease: [0.22, 1, 0.36, 1] }}
          />
          <motion.div
            className="tool-open-expander"
            style={{
              left: opening.rect.left,
              top: opening.rect.top,
              width: opening.rect.width,
              height: opening.rect.height,
              transformOrigin: 'top left',
            }}
            initial={{ x: 0, y: 0, scaleX: 1, scaleY: 1, borderRadius: 26 }}
            animate={{
              x: -opening.rect.left,
              y: -opening.rect.top,
              scaleX: window.innerWidth / Math.max(1, opening.rect.width),
              scaleY: window.innerHeight / Math.max(1, opening.rect.height),
              borderRadius: 0,
            }}
            transition={{ duration: 0.12, ease: [0.22, 1, 0.36, 1] }}
          />
        </>
      )}
    </section>
  );
}
